import { Autocomplete as BaseAutocomplete } from '@base-ui/react/autocomplete';
import type { ComponentProps } from 'react';
import { ChevronDownIcon, CrossIcon } from '../lib/icons.tsx';
import { LAYER } from '../lib/layer.ts';
import { cx, styled } from '../lib/styled.tsx';
import {
  controlSurface,
  groupLabelSurface,
  itemChoice,
  itemSurface,
  popupSeparator,
  popupSurface,
  popupTransition,
} from '../lib/surfaces.ts';

const popup =
  `${popupSurface} ${popupTransition} ` +
  'min-w-[var(--anchor-width)] max-h-[var(--available-height)] p-1';

function Icon({ children, className, ...props }: ComponentProps<typeof BaseAutocomplete.Icon>) {
  return (
    <BaseAutocomplete.Icon className={cx('flex shrink-0 text-ink-muted', className)} {...props}>
      {children ?? <ChevronDownIcon className="size-4" />}
    </BaseAutocomplete.Icon>
  );
}

function Clear({ children, className, ...props }: ComponentProps<typeof BaseAutocomplete.Clear>) {
  return (
    <BaseAutocomplete.Clear
      className={cx(
        'flex size-6 shrink-0 items-center justify-center rounded-control text-ink-muted',
        'hover:bg-surface-muted hover:text-ink',
        className,
      )}
      {...props}
    >
      {children ?? <CrossIcon className="size-3.5" />}
    </BaseAutocomplete.Clear>
  );
}

/**
 * Base UI keeps these two mounted at all times so screen readers announce their
 * changes reliably — they must not be hidden or conditionally rendered. That
 * makes their padding the caller's problem: put it on an inner element, or an
 * "empty" message that is not showing still reserves space in the popup.
 */
function Status({ children, className, ...props }: ComponentProps<typeof BaseAutocomplete.Status>) {
  return (
    <BaseAutocomplete.Status className={cx('', className)} {...props}>
      <div className="px-2.5 py-1.5 text-xs text-ink-muted">{children}</div>
    </BaseAutocomplete.Status>
  );
}

function Empty({ children, className, ...props }: ComponentProps<typeof BaseAutocomplete.Empty>) {
  return (
    <BaseAutocomplete.Empty className={cx('', className)} {...props}>
      <div className="px-2.5 py-6 text-center text-sm text-ink-muted">{children}</div>
    </BaseAutocomplete.Empty>
  );
}

/**
 * Suggestions under a free-text input: a search box, an address line, a tag
 * field. The typed text stands on its own — picking a suggestion is a shortcut,
 * not the only way to answer.
 *
 * Use {@link Combobox} when the value has to come from the list.
 */
export const Autocomplete = {
  /** State only — renders no element of its own. */
  Root: BaseAutocomplete.Root,
  InputGroup: styled(
    BaseAutocomplete.InputGroup,
    'flex w-full items-center gap-1.5 rounded-control border border-border-subtle bg-surface px-2 ' +
      'focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/25 ' +
      'data-invalid:border-danger data-invalid:ring-danger/20 ' +
      'data-disabled:bg-surface-muted data-disabled:opacity-60',
  ),
  Input: styled(
    BaseAutocomplete.Input,
    `${controlSurface} h-9 flex-1 border-0 bg-transparent px-1 ring-0 focus-visible:ring-0`,
  ),
  Trigger: styled(BaseAutocomplete.Trigger, 'flex shrink-0 items-center outline-none'),
  Icon,
  Clear,
  Value: styled(BaseAutocomplete.Value, 'truncate'),
  Portal: BaseAutocomplete.Portal,
  Backdrop: styled(BaseAutocomplete.Backdrop, 'fixed inset-0'),
  Positioner: styled(BaseAutocomplete.Positioner, `${LAYER.popup} outline-none`, { sideOffset: 6 }),
  Popup: styled(BaseAutocomplete.Popup, popup),
  Arrow: styled(BaseAutocomplete.Arrow, ''),
  Status,
  Empty,
  List: styled(BaseAutocomplete.List, 'max-h-[inherit] overflow-y-auto outline-none'),
  Row: styled(BaseAutocomplete.Row, 'flex'),
  Item: styled(BaseAutocomplete.Item, `${itemSurface} ${itemChoice}`),
  Separator: styled(BaseAutocomplete.Separator, popupSeparator),
  Group: styled(BaseAutocomplete.Group, ''),
  GroupLabel: styled(BaseAutocomplete.GroupLabel, groupLabelSurface),
  /** Renders the filtered items; virtualises long lists. */
  Collection: BaseAutocomplete.Collection,
};
